import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Card,
    Grid,
    Icon,
    Snackbar,
    Alert,
    Chip,
    Modal,
    Box,
    CircularProgress,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import DataTable from "examples/Tables/DataTable";
import TableSkeleton from 'components/CustomSkeleton/TableSkeleton';
import EmptyState from 'components/EmptyTable/EmptyState';
import TableActionMenu from 'components/TableActionMenu';
import MonoText from 'components/MonoText';
import { useCurrency } from 'context/CurrencyContext';
import { useAuth } from 'auth/Auth';
import { usePermission } from 'auth/usePermission';
import NewOrderCancellationModal from 'components/NewOrderCancellationModal';

const OrderCancellations = () => {
    const [cancellations, setCancellations] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [deleteDialog, setDeleteDialog] = useState({
        open: false,
        cancellation: null,
        loading: false,
    });

    const { axiosInstance } = useAuth();
    const navigate = useNavigate();
    const { canCreate, canDelete } = usePermission('order_cancellations');
    const { formatCurrency, currency: systemCurrency } = useCurrency();

    useEffect(() => {
        fetchCancellations();
    }, []);

    const fetchCancellations = async () => {
        setIsLoading(true);
        try {
            const response = await axiosInstance.get('/order-cancellations/');
            setCancellations(response.data || []);
        } catch (error) {
            console.error('Error fetching order cancellations:', error);
            setSnackbar({ open: true, message: 'Error loading order cancellations', severity: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    const handleOpenModal = () => setIsModalOpen(true);
    const handleCloseModal = () => setIsModalOpen(false);

    const handleSaveCancellation = async (payload) => {
        try {
            await axiosInstance.post('/order-cancellations/', payload);
            setSnackbar({ open: true, message: 'Order Cancellation created successfully', severity: 'success' });
            fetchCancellations();
        } catch (error) {
            console.error('Error saving order cancellation:', error);
            throw error;
        }
    };

    const handleRowClick = (rowData) => {
        navigate(`/order-cancellation/${rowData.id}`);
    };

    const handleDeleteClick = (cancellation) => {
        setDeleteDialog({
            open: true,
            cancellation,
            loading: false,
        });
    };

    const handleDeleteCancel = () => {
        if (deleteDialog.loading) return;
        setDeleteDialog({
            open: false,
            cancellation: null,
            loading: false,
        });
    };

    const handleDeleteConfirm = async () => {
        const { cancellation } = deleteDialog;
        if (!cancellation) return;

        setDeleteDialog(prev => ({ ...prev, loading: true }));
        try {
            await axiosInstance.delete(`/order-cancellations/${cancellation.id}`);
            setCancellations(prev => prev.filter(c => c.id !== cancellation.id));
            setSnackbar({ open: true, message: 'Order Cancellation deleted successfully', severity: 'success' });
            setDeleteDialog({ open: false, cancellation: null, loading: false });
        } catch (error) {
            console.error('Error deleting order cancellation:', error);
            const detail = error.response?.data?.detail;
            setSnackbar({
                open: true,
                message: typeof detail === 'string' ? detail : 'Error deleting order cancellation. Please try again.',
                severity: 'error',
            });
            setDeleteDialog(prev => ({ ...prev, loading: false }));
        }
    };

    const getStatusColor = (status) => {
        const statusMap = {
            'Active': 'success',
            'Approved': 'success',
            'Not Submitted': 'default',
            'Submitted': 'info',
            'Rejected': 'error',
            'Pending Approval': 'warning',
            'Approved-Not Processed': 'warning',
            'Processed': 'success'
        };
        return statusMap[status] || 'default';
    };

    const columns = useMemo(() => [
        {
            Header: "ID",
            accessor: "id",
            width: "6%",
            Cell: ({ row }) => (
                <MDTypography
                    component="span"
                    onClick={() => handleRowClick(row.original)}
                    variant="caption"
                    color="info"
                    fontWeight="medium"
                    sx={{ cursor: 'pointer' }}
                >
                    {`#${row.original.id}`}
                </MDTypography>
            ),
        },
        {
            Header: "Cancellation Name",
            accessor: "name",
            width: "15%",
            Cell: ({ value, row }) => (
                <MDTypography
                    component="span"
                    variant="caption"
                    color="info"
                    fontWeight="medium"
                    sx={{
                        cursor: 'pointer',
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                        lineHeight: 1.35,
                        display: 'inline-block',
                        maxWidth: '100%',
                    }}
                    onClick={() => handleRowClick(row.original)}
                >
                    {value || '-'}
                </MDTypography>
            ),
        },
        {
            Header: "Travel Order",
            accessor: (row) => row.travel_order?.name || (row.travel_order_id ? `TO-${row.travel_order_id}` : ''),
            id: "travel_order",
            width: "12%",
            Cell: ({ row }) => {
                const toId = row.original.travel_order_id;
                const toName = row.original.travel_order?.name || (toId ? `TO-${toId}` : null);
                if (!toId) return <MDTypography variant="caption">-</MDTypography>;
                return (
                    <MDTypography
                        component="span"
                        variant="caption"
                        color="info"
                        fontWeight="medium"
                        sx={{
                            cursor: 'pointer',
                            whiteSpace: 'normal',
                            wordBreak: 'break-word',
                            lineHeight: 1.35,
                            display: 'inline-block',
                            maxWidth: '100%',
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/travel-order/${toId}`);
                        }}
                    >
                        {toName}
                    </MDTypography>
                );
            },
        },
        {
            Header: "Invoice",
            accessor: (row) => row.invoice?.invoice_number || row.invoice?.name || (row.invoice_id ? `#${row.invoice_id}` : ''),
            id: "invoice",
            width: "10%",
            Cell: ({ row }) => {
                const invId = row.original.invoice_id;
                const invName = row.original.invoice?.invoice_number || row.original.invoice?.name || (invId ? `#${invId}` : null);
                if (!invId || !invName) return <MDTypography variant="caption">-</MDTypography>;
                return (
                    <MDTypography
                        component="span"
                        variant="caption"
                        color="info"
                        fontWeight="medium"
                        sx={{
                            cursor: 'pointer',
                            whiteSpace: 'normal',
                            wordBreak: 'break-word',
                            lineHeight: 1.35,
                            display: 'inline-block',
                            maxWidth: '100%',
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/invoices/${invId}`);
                        }}
                    >
                        {invName}
                    </MDTypography>
                );
            },
        },
        {
            Header: "Order Line Item",
            accessor: (row) => row.order_line_item?.name || (row.order_line_item_id ? `#${row.order_line_item_id}` : ''),
            id: "order_line_item",
            width: "11%",
            Cell: ({ row }) => {
                const itemName = row.original.order_line_item?.name || (row.original.order_line_item_id ? `#${row.original.order_line_item_id}` : null);
                if (!itemName) return <MDTypography variant="caption">-</MDTypography>;
                return <MDTypography variant="caption">{itemName}</MDTypography>;
            },
        },
        {
            Header: "Cancellation Type",
            accessor: "cancellation_type",
            width: "14%",
            Cell: ({ value }) => <MDTypography variant="caption">{value || '-'}</MDTypography>,
        },
        {
            Header: "Status",
            accessor: "status",
            width: "8%",
            Cell: ({ value }) => (
                <Chip
                    label={value || 'Active'}
                    size="small"
                    color={getStatusColor(value)}
                    sx={{ fontWeight: 600, fontSize: "0.65rem", height: 20 }}
                />
            ),
        },
        {
            Header: "Refund Status",
            accessor: "refund_status",
            width: "11%",
            Cell: ({ value }) => {
                const colorMap = {
                    'Refunded': 'success',
                    'Initiated': 'warning',
                    'Processing': 'warning',
                    'Failed': 'error',
                    'NA': 'default'
                };
                return (
                    <Chip
                        label={value || 'NA'}
                        size="small"
                        color={colorMap[value] || 'default'}
                        sx={{ fontWeight: 600, fontSize: "0.65rem", height: 20 }}
                    />
                );
            },
        },
        {
            Header: "Refund Amount",
            accessor: "refund_amount",
            width: "8%",
            align: "right",
            Cell: ({ value, row }) => {
                if (value == null || value === '') return <MonoText align="right">-</MonoText>;
                const curr = row.original?.currency || row.original?.travel_order?.currency || systemCurrency || 'USD';
                return <MonoText align="right">{formatCurrency(value, curr)}</MonoText>;
            },
        },
        {
            Header: "Actions",
            accessor: "actions",
            width: "5%",
            align: "center",
            disableSortBy: true,
            Cell: ({ row }) => (
                canDelete ? (
                    <TableActionMenu
                        row={row.original}
                        onView={handleRowClick}
                        onDelete={() => handleDeleteClick(row.original)}
                    />
                ) : (
                    <TableActionMenu
                        row={row.original}
                        onView={handleRowClick}
                    />
                )
            ),
        },
    ], [canDelete]);

    const tableData = useMemo(() => ({
        columns,
        rows: cancellations,
    }), [columns, cancellations]);

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={6} pb={3}>
                <Grid container spacing={6}>
                    <Grid item xs={12}>
                        <Card>
                            <MDBox
                                mx={2}
                                mt={-3}
                                py={3}
                                px={2}
                                variant="gradient"
                                bgColor="info"
                                borderRadius="lg"
                                coloredShadow="info"
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                            >
                                <MDTypography variant="h6" color="white">Order Cancellations</MDTypography>
                                <MDButton
                                    variant="gradient"
                                    color="dark"
                                    onClick={handleOpenModal}
                                    disabled={!canCreate}
                                >
                                    <Icon sx={{ fontWeight: "bold" }}>add</Icon>
                                    &nbsp;Add Cancellation
                                </MDButton>
                            </MDBox>
                            <MDBox pt={3}>
                                {isLoading ? (
                                    <TableSkeleton />
                                ) : cancellations.length > 0 ? (
                                    <DataTable
                                        table={tableData}
                                        isSorted={true}
                                        entriesPerPage={{ defaultValue: 10, entries: [5, 10, 15, 20, 25] }}
                                        canSearch
                                        showTotalEntries
                                        noEndBorder
                                    />
                                ) : (
                                    <EmptyState moduleName="Order Cancellation" canCreate={canCreate} />
                                )}
                            </MDBox>
                        </Card>
                    </Grid>
                </Grid>
            </MDBox>

            <NewOrderCancellationModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                onSave={handleSaveCancellation}
            />

            <Modal
                open={deleteDialog.open}
                onClose={handleDeleteCancel}
                aria-labelledby="delete-cancellation-modal"
            >
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: { xs: '90%', sm: 450 },
                        bgcolor: 'background.paper',
                        boxShadow: 24,
                        p: 4,
                        borderRadius: 2,
                    }}
                >
                    <MDTypography variant="h6" component="h2" mb={2}>
                        Delete Order Cancellation
                    </MDTypography>
                    <MDTypography variant="body2" color="text" mb={3}>
                        Are you sure you want to delete order cancellation <strong>{deleteDialog.cancellation?.name}</strong>? This action cannot be undone.
                    </MDTypography>
                    <MDBox display="flex" justifyContent="flex-end" gap={2}>
                        <MDButton
                            onClick={handleDeleteCancel}
                            variant="outlined"
                            color="secondary"
                            disabled={deleteDialog.loading}
                        >
                            Cancel
                        </MDButton>
                        <MDButton
                            onClick={handleDeleteConfirm}
                            variant="gradient"
                            color="error"
                            disabled={deleteDialog.loading}
                        >
                            {deleteDialog.loading ? (
                                <CircularProgress size={20} color="inherit" />
                            ) : (
                                'Delete'
                            )}
                        </MDButton>
                    </MDBox>
                </Box>
            </Modal>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </DashboardLayout>
    );
};

export default OrderCancellations;
